import React, { useCallback, useContext, useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { makeSafeRequest } from '../../../common/makeSafeRequest';
import Preloader from '../../../components/common/Preloader';
import { AppInfoContext } from '../../../components/contexts/AppInfoContext';
import SystemPage from '../../../components/system-page/SystemPage';
import { ListForcePackageData, Optional } from '../../../types/common';
import { FiltersData, InputData, SensorData } from '../../../types/systems';
import { defaultSettings, sensorsSettingsTemplate } from './defaultSettings';

const Sensors: React.FC = () => {
	const [filters, setFilters] = useState<FiltersData>();
	const [inputData, setInputData] = useState<InputData>();
	const { currentPid } = useContext(AppInfoContext);

	const initialize = useCallback(
		async (pid: number, fp: number | string, side: number | string) => {
			const filters = await downloadFilters(pid, fp, side);
			const inpData = await downloadInputData();

			if (!filters || !inpData) {
				return false;
			}

			setFilters(filters);
			setInputData(inpData);
			return true;
		},
		[]
	);

	const downloadFilters = async (pid: number, fp: number | string, side: number | string) => {
		return await makeSafeRequest<FiltersData>('sensors/', 'GET', {
			params: { pid: pid, fp: fp, side: side },
		});
	};

	const downloadInputData = async () => {
		return await makeSafeRequest<InputData>('sensors/input-data/', 'GET');
	};

	const downloadSensorSettings = async (systemId: number) => {
		return await makeSafeRequest<SensorData>(`sensors/${systemId}/`, 'GET');
	};

	const addSensor = async (settings: Optional<SensorData, 'id'>) => {
		return await makeSafeRequest<SensorData>('sensors/', 'POST', {
			payload: settings,
			onSuccessMessage: 'Sensor added successfully!',
		});
	};

	const deleteSensor = async (settingsId: number) => {
		return await makeSafeRequest<null>(`sensors/${settingsId}/`, 'DELETE', {
			onSuccessMessage: 'Sensor deleted successfully!',
		});
	};

	const saveSensor = async (settings: Optional<SensorData, 'id'>) => {
		if (!validateSettings(settings)) return null;

		return await makeSafeRequest<SensorData>(`sensors/${settings.id}/`, 'PUT', {
			payload: settings,
			onSuccessMessage: 'Sensor saved successfully!',
		});
	};

	const duplicateSensor = async (settingsId: number) => {
		return await makeSafeRequest<SensorData>(`sensors/${settingsId}/duplicate/`, 'POST', {
			onSuccessMessage: 'Sensor duplicated successfully!',
		});
	};

	const validateSettings = (settings: Optional<SensorData, 'id'>) => {
		if (settings?.rcs_probability?.some((el) => el.range < 0)) {
			notifications.show({
				title: 'Error',
				message: "You haven't set all the range values!",
				color: 'red',
				autoClose: 5000,
			});
			return false;
		}
		return true;
	};

	useEffect(() => {
		initialize(currentPid, '', '');
	}, [initialize, currentPid]);

	return filters && inputData ? (
		<SystemPage
			name="sensor"
			forcePackageData={filters.fps as ListForcePackageData[]}
			systemsData={filters.sensors as SensorData[]}
			inputData={inputData}
			initializeFilters={initialize}
			downloadSystemSettings={downloadSensorSettings}
			systemSettingsTemplate={sensorsSettingsTemplate}
			defaultSettings={defaultSettings}
			addSystem={addSensor}
			saveSystem={saveSensor}
			deleteSystem={deleteSensor}
			duplicateSystem={duplicateSensor}
		></SystemPage>
	) : (
		<Preloader></Preloader>
	);
};

export default Sensors;
